import _ from "lodash";

import bridge from "@/api/bridge";
import cloud from "@/api/cloud";
import { set } from "@/utils/vuex";
import "@k4connect/k4model2-js";

import eventsModule from "./events";
import nodesModule from "./nodes";

const States = {
	NOT_AVAILABLE: -2,
	NOT_FOUND: -1,
	UNKNOWN: 0,
	DISCOVERED: 1,
	CONNECTED: 2,
};

const NonTerminalStates = [States.UNKNOWN, States.DISCOVERED];

const getInitState = () => ({
	model: null,
	system: null,
	status: States.UNKNOWN,
	serial: null,
	host: null,
	ip: null,
	discoveryType: null,
	local: null,
	credentials: null,
});

export default {
	namespaced: true,
	state: getInitState(),
	modules: {
		events: eventsModule,
		nodes: nodesModule,
	},

	getters: {
		state: state => state.status,
		states: () => States,

		// State in string form
		status: state => getStateName(state.status),

		connecting: state => _.includes(NonTerminalStates, state.status),
	},

	mutations: {
		setModel(state, model) {
			state.model = model;

			// Expose K4 model as a global for interactive debugging
			window.k4 = model;
		},

		setSystem: set("system"),
		setSerial: set("serial"),
		setHost: set("host"),
		setIp: set("ip"),
		setDiscoveryType: set("discoveryType"),
		setLocal: set("local"),
		setCredentials: set("credentials"),

		setStatus(state, status) {
			state.status = status;
			this._vm.$log(
				`Controller: State change, ip ${state.ip}, status ${getStateName(
					state.status
				)} [${state.system}]`
			);
		},

		resetState(state) {
			if (state.model) {
				state.model.close();
			}

			Object.assign(state, getInitState());
		},
	},

	actions: {
		init() {
			// NOTE: Need this noop action so that init actions in submodules
			// will be called properly. This isn't vuex specific, it's a side-
			// effect of how `utils/vuex.js#forEachModule` was written, which
			// doesn't take submodules into account.
		},

		reset({ commit, dispatch }) {
			commit("resetState");
			dispatch("modelChanged", { initialConnection: true }, { root: true });
		},

		async getModel({ state, dispatch, rootState }) {
			const localStr = state.local ? "local" : "remote";
			this._vm.$log(
				`Controller: Get ${localStr} model at ${state.ip} [${state.system}]`
			);

			// if we have never discovered this controller, load again
			if (state.status === States.NOT_FOUND) {
				let room = rootState.install.room;
				if (state.local) await dispatch("loadLocal", room.id);
				else await dispatch("loadRemote", room.id);
			}

			await dispatch("waitForOnline");
			return state.model;
		},

		async reprovision({ state, dispatch, commit }) {
			let { ip, serial, credentials, system } = state;

			this._vm.$log(
				`Controller: Reprovisioning controller at ${ip} [${system}]`
			);

			await dispatch("waitForOnline");

			// force close model connection, we dont want it trying to reconnect and cause an auth race condition
			state.model.close();

			// send reprovision command
			await bridge.reprovisionController(ip, serial);

			return new Promise((resolve, reject) => {
				// wait for controller to settle - reprovision resolves when websockets are listening, but sometimes model server isnt quite ready
				setTimeout(async () => {
					// connect to k4model
					let model = await dispatch("connect", { ip, credentials });
					commit("setModel", model);

					this._vm.$log(
						`Controller: Reprovision of controller at ${ip} complete [${system}]`
					);
					resolve();
				}, 20000);
			});
		},

		async waitForOnline({ state }) {
			this._vm.$log(
				`Controller: Waiting for controller at ${
					state.ip
				}, status ${getStateName(state.status)} [${state.system}]`
			);
			return new Promise((resolve, reject) => {
				if (state.model && state.status === States.CONNECTED) {
					resolve();
				} else if (
					state.status === States.UNKNOWN ||
					state.status === States.DISCOVERED
				) {
					this._vm.$log(
						`Controller: Waiting, 5 second delay, for controller at ${state.ip} [${state.system}]`
					);
					// allow connect to finish
					setTimeout(() => {
						if (state.model && state.status === States.CONNECTED) {
							resolve();
						} else {
							this._vm.$log(
								`Controller: Waiting, controller at ${
									state.ip
								} not found after 5 second delay, status: ${getStateName(
									state.status
								)} [${state.system}]`
							);
							reject(new Error("Not connected"));
						}
					}, 5000);
				} else {
					reject(new Error("Not connected"));
				}
			});
		},

		loadLocal(ctx, systemId) {
			// Clear up any old configuration
			ctx.dispatch("reset");
			ctx.commit("setLocal", true);
			ctx.commit("setSystem", systemId);

			this._vm.$log(`Controller: Loading local ${systemId}`);

			// Prevent browser clients from attempting to connect to local
			// controller.
			const nativeApp = ctx.rootGetters["config/inMobileApp"];
			if (!nativeApp) {
				this._vm.$log(
					`Controller: Can't connect to local controller from browser [${systemId}]`
				);
				ctx.commit("setStatus", States.NOT_AVAILABLE);
				return;
			}

			// Call shared code with our local find method.
			return loadController.call(this, ctx, systemId, ({ serial }) =>
				bridge.findController(serial, systemId)
			);
		},

		loadRemote(ctx, systemId) {
			// Clear up any old configuration
			ctx.dispatch("reset");
			ctx.commit("setLocal", false);
			ctx.commit("setSystem", systemId);

			this._vm.$log(`Controller: Loading remote ${systemId}`);

			// Call shared code with our remote find method.
			return loadController.call(this, ctx, systemId, ({ credentials }) =>
				cloud
					.getControllerRemote(systemId, credentials.ctoken)
					.then(({ host, port }) => ({
						type: "ec2",
						ip: `${host}:${port || 443}`,
						host,
						local: false,
					}))
			);
		},

		connect({ state, commit, dispatch }, { ip, credentials }) {
			let {
				username,
				device,
				sysid,
				ctoken,
				password,
				analytics,
			} = credentials;

			this._vm.$log(
				`Controller: Connecting to ${ip} as user ${username} [${sysid}]`
			);

			// Unfortunately we need to tack on the port when connecting to local controllers.
			if (state.local) ip += ":8000";

			// eslint-disable-next-line no-undef
			const model = new K4({
				url: `https://${ip}`,
				username,
				from: device,
				system: sysid,
				ctoken,
				password,
				analytics,
				listeners: {
					ready: k4 => {
						commit("setStatus", States.CONNECTED);
						dispatch(
							"modelChanged",
							{ initialConnection: true },
							{ root: true }
						);
						this._vm.$log(`Controller: K4Model connected at ${ip} [${sysid}]`);
					},
					error: err => {
						commit("setStatus", States.NOT_FOUND);
						this._vm.$log(
							`Controller: K4Model error ${err} at ${ip} [${sysid}]`
						);
					},
					disconnected: () => {
						commit("setStatus", States.UNKNOWN);
						this._vm.$log(
							`Controller: K4Model disconnected from ${ip} [${sysid}]`
						);
					},
				},
			});

			// Listen for any changes to the underlying model and update our state accordingly.
			model.model.on(
				"value",
				// Model 'value' events usually fire in quick succession.
				// Rebuilding the vuex state with a new k4 model is expensive so
				// we debounce to make sure we're not doing it too frequently.
				_.debounce(
					() =>
						dispatch(
							"modelChanged",
							{ initialConnection: false },
							{ root: true }
						),
					1000,
					{
						maxWait: 5000,
					}
				)
			);

			// Listen in on all unhandled events.
			model.model.on("fire", (event, data) => {
				dispatch("modelEventFired", { event, data }, { root: true });
			});

			return model;
		},
	},
};

// Shared "execute around" code for loading a controller. The entry points are
// the 'controller/loadLocal' and 'controller/loadRemote' actions, which pass
// in their own strategy for locating the controller.
async function loadController({ commit, dispatch }, systemId, findFn) {
	let credentials, serial, controller;
	try {
		[credentials, serial] = await Promise.all([
			cloud.getSystemAdmin(systemId),
			cloud.getControllerSerial(systemId),
		]);

		// Call passed finder function to locate controller with provided strategy.
		controller = await findFn({ serial, credentials });
		if (!controller) {
			throw new Error("Not found");
		}
	} catch (e) {
		this._vm.$log("Controller: Failed to locate controller");
		this._vm.$log(e.toString());
		commit("setStatus", States.NOT_FOUND);
		return;
	}

	commit("setCredentials", credentials);
	commit("setSerial", serial);
	commit("setStatus", States.DISCOVERED);

	// Save connection info
	let { type, ip, host, local } = controller;
	commit("setDiscoveryType", type);
	commit("setIp", ip);
	commit("setHost", host);
	commit("setLocal", local);

	this._vm.$log(
		`Controller: Found controller at ${ip} via ${type} [${systemId}]`
	);

	// Connect to model
	let model = await dispatch("connect", {
		ip: controller.ip,
		credentials,
	});
	commit("setModel", model);
}

function getStateName(status) {
	for (let i in States) {
		if (States[i] === status) {
			return i;
		}
	}
}
